import {
    Button as MUIButton
} from '@mui/material';
import {
    type SxProps,
    type Theme,
    darken,
    useTheme
} from '@mui/material/styles';
import {
    type FC
} from 'react';

import Typography from '../Typography';

import type {
    ButtonProps,
    ButtonSX,
    ButtonSxObject
} from './types';

import styles from './styles';

/**
 * Resolves the restricted `sx` prop into a single plain object.
 * Supports object, array, or theme callback.
 */
const resolveSx = (sx: ButtonSX | undefined, theme: Theme): ButtonSxObject => {
    if (!sx) { return {}; }

    if (typeof sx === 'function') { return sx(theme); }

    if (Array.isArray(sx)) {
        return sx.reduce<ButtonSxObject>(
            (accumulated, current) => {
                if (!current) { return accumulated; }

                const currentObject = typeof current === 'function' ? current(theme) : current;

                return {
                    ...accumulated,
                    ...currentObject
                };
            },
            {}
        );
    }

    return sx;
};

const Button: FC<ButtonProps> = (props) => {
    const {
        children,
        isMobileFullWidth,
        sx,
        typographyVariant = 'button',
        variant = 'contained',
        ...remainingProps
    } = props;

    const theme = useTheme();

    // Resolve the incoming sx into a single object
    const resolvedSx = resolveSx(sx, theme);

    // Derive styling overrides without nested ternaries
    const backgroundColor = typeof resolvedSx.backgroundColor === 'string'
        ? resolvedSx.backgroundColor
        : undefined;

    const textColor = typeof resolvedSx.color === 'string' ? resolvedSx.color : undefined;

    let borderColor: string | undefined;

    if (backgroundColor === 'transparent') {
        borderColor = textColor;
    } else if (backgroundColor) {
        borderColor = backgroundColor;
    }

    const forcedTextColor = textColor ? `${textColor} !important` : undefined;

    // 3. Build the sx array without booleans, properly typed
    const computedSx = [
        styles.button,
        variant ? styles[variant] : undefined,
        isMobileFullWidth ? styles.fullWidth : undefined,
        {
            ':hover': {
                backgroundColor: (variant === 'contained' && !!backgroundColor) ? darken(backgroundColor, 0.2) : undefined
            },

            borderColor,
            color: forcedTextColor
        },
        resolvedSx
    ].filter(
        (style): style is NonNullable<typeof style> => style !== undefined
    ) as SxProps<Theme>;

    return (
        <MUIButton
            {...remainingProps}
            sx={computedSx}
            variant={variant}
        >
            <Typography
                sx={{
                    color: 'inherit'
                }}
                variant={typographyVariant}
            >
                {children}
            </Typography>
        </MUIButton>
    );
};

export default Button;
